import { triggerSimpleAjax } from "../../helpers/httpHelper";
import { SAVE_OUTLET_IMAGES, GET_OUTLET_DATA } from "../../helpers/constants/apiUrl";

/**
 * Save uploaded images to an outlet's workflow
 * @param {Object} params - Parameters for saving images
 * @param {string} params.outlet_slug - Outlet slug identifier
 * @param {string[]} params.imageIds - Array of uploaded image IDs
 * @param {string} params.questionId - Optional specific question ID
 * @param {string} params.workflow_object_id - Optional workflow object ID (if not provided, will fetch from API)
 * @returns {Promise<Object>} - Promise that resolves with save response
 */
export const saveOutletImages = ({ outlet_slug, imageIds, questionId = null, workflow_object_id = null }) => {
    return new Promise((resolve, reject) => {
        if (!outlet_slug) {
            reject(new Error('Missing outlet_slug'));
            return;
        }

        if (!imageIds || imageIds.length === 0) {
            reject(new Error('No image IDs provided'));
            return;
        }

        // If workflow_object_id is provided, use it directly
        if (workflow_object_id) {
            // If questionId is also provided, save directly
            if (questionId) {
                const payload = {
                    question_id: questionId,
                    workflow_object_id: workflow_object_id,
                    images: imageIds
                };

                triggerSimpleAjax(
                    SAVE_OUTLET_IMAGES,
                    'POST',
                    payload,
                    (response) => {
                        resolve(response);
                    },
                    (error) => {
                        reject(error);
                    }
                );
                return;
            }
        }

        // Fallback: Fetch outlet data to get question_id and/or workflow_object_id
        triggerSimpleAjax(
            `${GET_OUTLET_DATA}${outlet_slug}/`,
            'GET',
            null,
            (outletData) => {
                // Extract workflow_object_id if not provided
                const fetchedWorkflowObjectId = workflow_object_id || outletData?.related_workflow_object?.id;
                
                if (!fetchedWorkflowObjectId) {
                    reject(new Error('Missing workflow_object_id from API response'));
                    return;
                }

                // Find image upload type questions from question_and_answers
                const questionAndAnswers = outletData?.question_and_answers || {};
                const imageUploadQuestions = [];

                // Iterate through all question groups
                Object.keys(questionAndAnswers).forEach(groupKey => {
                    const questions = questionAndAnswers[groupKey];
                    if (Array.isArray(questions)) {
                        questions.forEach(question => {
                            // Check if question type is for image upload
                            if (question.type === 'multiple_image' || 
                                question.type === 'image' || 
                                question.type === 'single_image' ||
                                question.type === 'image_upload') {
                                imageUploadQuestions.push({
                                    id: question.id,
                                    name: question.name,
                                    type: question.type
                                });
                            }
                        });
                    }
                });

                if (imageUploadQuestions.length === 0) {
                    reject(new Error('No image upload questions found for this outlet'));
                    return;
                }

                // If there's a specific questionId prop, use it; otherwise use the first image question found
                let targetQuestionId = questionId;
                if (!targetQuestionId && imageUploadQuestions.length > 0) {
                    targetQuestionId = imageUploadQuestions[0].id;
                }

                const payload = {
                    question_id: targetQuestionId,
                    workflow_object_id: fetchedWorkflowObjectId,
                    images: imageIds
                };

                triggerSimpleAjax(
                    SAVE_OUTLET_IMAGES,
                    'POST',
                    payload,
                    (response) => {
                        resolve(response);
                    },
                    (error) => {
                        reject(error);
                    }
                );
            },
            (error) => {
                reject(error);
            }
        );
    });
};
